import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Shield, Lock, TrendingUp, ArrowRight, CheckCircle, Users, Clock, Award, Building2, FileCheck } from "lucide-react";

const VKB_LOGO_URL = "https://customer-assets.emergentagent.com/job_ingenieur-agency/artifacts/7oos7e3e_VKBayern_logo.svg.png";
const DQS_LOGO_URL = "https://customer-assets.emergentagent.com/job_3d119e6f-14f2-49d0-b48b-21438dbeafee/artifacts/b0frwk4w_3xp0oxvy_medium_DQS_en.png";
const MANSFELD_IMAGE = "https://customer-assets.emergentagent.com/job_3d119e6f-14f2-49d0-b48b-21438dbeafee/artifacts/8u6zz0by_490df1d7-396a-450c-8096-d69de69384f5.JPG";
const MANSFELD_VKB_GEBAEUDE = "https://customer-assets.emergentagent.com/job_3d119e6f-14f2-49d0-b48b-21438dbeafee/artifacts/cl8hh574_ChatGPT%20Image%2026.%20Ma%CC%88rz%202026%2C%2011_31_35.png";
const MANSFELD_TEAM = "https://customer-assets.emergentagent.com/job_3d119e6f-14f2-49d0-b48b-21438dbeafee/artifacts/v6u41d5v_ChatGPT%20Image%2026.%20Ma%CC%88rz%202026%2C%2014_15_58.png";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_3d119e6f-14f2-49d0-b48b-21438dbeafee/artifacts/jk90s8h1_ChatGPT%20Image%2028.%20Apr.%202026%2C%2013_17_08.png";
const TEAM_IMAGE = "https://images.unsplash.com/photo-1606836591695-4d58a73eba1e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

export default function HomePage() {
  const services = [
    {
      icon: Shield,
      title: "Berufshaftpflicht für Architekten und Ingenieure",
      description: "Die Berufshaftpflichtversicherung schützt Sie vor Risiken wie Planungs-, Beratungs-, Bauüberwachungs- oder Koordinationsfehler, denen Sie als Architekt oder Ingenieur täglich ausgesetzt sind. Sie ist Kernstück Ihrer Existenzsicherung.",
      link: "/leistungen#berufshaftpflicht"
    },
    {
      icon: Lock,
      title: "Cyberschutz",
      description: "Ob Daten der Kunden, vertrauliche betriebliche Informationen oder Steuerung der internen Prozesse – Unternehmen, ihre Vertreter und Mitarbeiter bewegen sich permanent in der digitalen Welt. Dabei erhöhen sich die Angriffe und Attacken auf digitale Systeme und Daten rasant.",
      link: "/leistungen#cyber"
    },
    {
      icon: TrendingUp,
      title: "Betriebliche Altersvorsorge",
      description: "Die betriebliche Altersvorsorge ist ein unverzichtbarer Baustein moderner Personalpolitik. Sie eröffnet eine attraktive Möglichkeit, Ihren Mitarbeitern eine wertvolle Zusatzleistung zu bieten und gleichzeitig für die eigene Zukunft vorzusorgen.",
      link: "/leistungen#altersvorsorge"
    }
  ];

  const processSteps = [
    {
      step: "01",
      title: "Risiko-Check",
      description: "In einem kurzen Gespräch (ca. 15 Minuten) analysieren wir Ihre aktuelle Situation und identifizieren potenzielle Risiken.",
      icon: FileCheck
    },
    {
      step: "02",
      title: "Konzept & Deckungslücken",
      description: "Wir erstellen ein maßgeschneidertes Konzept und schließen eventuell bestehende Deckungslücken.",
      icon: Shield
    },
    {
      step: "03",
      title: "Laufende Betreuung",
      description: "Bei Änderungen in Ihrem Büro oder im Schadenfall sind wir Ihr verlässlicher Ansprechpartner.",
      icon: Users
    }
  ];

  const stats = [
    { number: "25+", label: "Jahre Erfahrung" },
    { number: "100%", label: "Spezialisierung" },
    { number: "1", label: "Fester Ansprechpartner" }
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-[90vh] flex items-center overflow-hidden"
      >
        {/* Solid Blue Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#003B70] to-[#002855]" />
        
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }} />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Sichere Planung.<br />
                <span className="text-sky-300">Starke Absicherung.</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-white/90 mb-4 font-medium">
                Ihre Fachagentur für Architekten und Ingenieure
              </p>
              
              <p className="text-lg text-white/80 mb-8 max-w-xl leading-relaxed">
                Als Experten für Berufshaftpflichtversicherungen stehen wir Architekten und Ingenieuren 
                beratend zur Seite. Unser Fokus liegt auf maßgeschneiderten und individuellen Lösungen. 
                Unser starker Partner hierbei ist die Versicherungskammer Bayern.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Link to="/kontakt">
                  <Button 
                    data-testid="hero-cta-primary"
                    className="bg-white text-[#003B70] hover:bg-slate-100 px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                  >
                    Jetzt Beratung anfragen
                  </Button>
                </Link>
                <Link to="/leistungen">
                  <Button 
                    data-testid="hero-cta-secondary"
                    variant="outline"
                    className="border-white/40 text-white hover:bg-white/10 px-8 py-6 text-lg backdrop-blur-sm"
                  >
                    Leistungen ansehen
                  </Button>
                </Link>
              </div>
              
              <div className="grid grid-cols-2 gap-y-3 gap-x-8 text-white/80">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-sky-300 flex-shrink-0" />
                  <span className="text-sm">Spezialisiert auf Planungsrisiken</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-sky-300 flex-shrink-0" />
                  <span className="text-sm">Direkte Betreuung</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-sky-300 flex-shrink-0" />
                  <span className="text-sm">Bundesweit tätig</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-sky-300 flex-shrink-0" />
                  <span className="text-sm">Expertise seit 25+ Jahren</span>
                </div>
              </div>
            </div>
            
            {/* Berater Image - Circular Cropped */}
            <div className="flex justify-center animate-fade-in-up animation-delay-200 mt-4 lg:mt-0">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-sky-400/30 to-blue-600/30 rounded-full blur-3xl" />
                <div className="relative w-64 h-64 sm:w-72 sm:h-72 xl:w-80 xl:h-80 rounded-full overflow-hidden border-4 border-white/20 shadow-2xl">
                  <img 
                    src={HERO_IMAGE}
                    alt="Jens-Oliver Mansfeld und Team"
                    className="w-full h-full object-cover scale-125 object-top"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 bg-white p-4 rounded-xl shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#003B70]/10 flex items-center justify-center">
                      <Award className="h-5 w-5 text-[#003B70]" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">Jens-Oliver Mansfeld</p>
                      <p className="text-xs text-slate-500">Ihr persönlicher Berater</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/60 rounded-full" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative -mt-16 z-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="flex flex-col items-center justify-end text-center py-4 h-32 md:h-36">
              <img src={VKB_LOGO_URL} alt="VKB" className="h-20 md:h-24 w-auto mb-auto" />
              <p className="text-slate-600 text-sm font-medium mt-2">Starker Partner</p>
            </div>
            {stats.map((stat, index) => (
              <div key={index} className="flex flex-col items-center justify-end text-center py-4 h-32 md:h-36">
                <p className="text-3xl md:text-4xl font-bold text-[#003B70] mb-auto mt-auto">{stat.number}</p>
                <p className="text-slate-600 text-sm font-medium mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#003B70] font-semibold text-sm uppercase tracking-wider">Unsere Leistungen</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4">
              Leistungsbausteine für Architekten & Ingenieure
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Wir kennen die Risiken Ihrer Branche und bieten Ihnen passgenaue Beratung 
              mit Weitsicht und Erfahrung.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                data-testid={`service-card-${index}`}
                className="group bg-white p-8 rounded-xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 relative overflow-hidden"
              >
                {/* Hover Background Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#003B70]/5 to-sky-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#003B70] to-[#002855] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <service.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-4 group-hover:text-[#003B70] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <Link 
                    to={service.link}
                    className="inline-flex items-center text-[#003B70] font-medium group-hover:gap-3 transition-all"
                  >
                    Mehr erfahren
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DQS Certificate Section */}
      <section data-testid="dqs-section" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-slate-900 to-[#003B70] rounded-2xl p-10 md:p-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-sky-300 font-semibold text-sm uppercase tracking-wider">Qualität & Vertrauen</span>
                <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-6">
                  Zertifizierte Beratungsqualität
                </h2>
                <p className="text-lg text-white/80 mb-8 leading-relaxed">
                  Die Versicherungskammer Bayern ist der größte öffentliche Versicherer Deutschlands. 
                  Als Teil dieses Konzerns stehen wir für geprüfte Qualität und höchste Standards 
                  in der Kundenberatung. Das DQS-Zertifikat bestätigt unsere Kompetenz und unser 
                  Engagement für exzellenten Service.
                </p>
                <div className="flex flex-wrap gap-6">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-6 w-6 text-sky-300" />
                    <span className="text-white">Geprüfte Beratungsqualität</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-6 w-6 text-sky-300" />
                    <span className="text-white">Zertifizierte Prozesse</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-6 w-6 text-sky-300" />
                    <span className="text-white">Kontinuierliche Verbesserung</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-center lg:justify-end">
                <div className="bg-white p-8 rounded-2xl shadow-2xl">
                  <img 
                    src={DQS_LOGO_URL}
                    alt="DQS Zertifiziert"
                    className="h-40 md:h-48 w-auto"
                  />
                  <p className="text-center text-slate-600 mt-4 font-medium">Zertifiziertes Management</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Existenzschutz Section */}
      <section data-testid="existenzschutz-section" className="py-24 bg-slate-50 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute right-0 top-0 w-1/2 h-full opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(#003B70 1px, transparent 1px),
                              linear-gradient(90deg, #003B70 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#003B70] font-semibold text-sm uppercase tracking-wider">Warum wir?</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-6">
                Worum es wirklich geht:<br />
                <span className="text-[#003B70]">Existenzschutz im Projektgeschäft</span>
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Als Architekt und Ingenieur treffen Sie bei Ihrer Arbeit stets auf eine große Anzahl 
                von Gesetzen und Verordnungen. Ungewollt einen Verstoß zu begehen, kann man da nicht ausschließen.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Darüber hinaus sind Justiz- und Verwaltungsbehörden verpflichtet, einer Sache schon bei 
                einem bloßen Verdacht nachzugehen. Ein guter Rechtsbeistand hilft Ihnen, sich gegen dieses 
                spezielle Berufsrisiko im Baugewerbe abzusichern.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-start gap-4 p-4 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 rounded-lg bg-[#003B70]/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-6 w-6 text-[#003B70]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Planungsfehler</h4>
                    <p className="text-sm text-slate-600">Umfassend abgesichert</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 rounded-lg bg-[#003B70]/10 flex items-center justify-center flex-shrink-0">
                    <Users className="h-6 w-6 text-[#003B70]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Koordination</h4>
                    <p className="text-sm text-slate-600">Zuverlässig geschützt</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 rounded-lg bg-[#003B70]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-[#003B70]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Fristen</h4>
                    <p className="text-sm text-slate-600">Immer eingehalten</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 rounded-lg bg-[#003B70]/10 flex items-center justify-center flex-shrink-0">
                    <Award className="h-6 w-6 text-[#003B70]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Qualität</h4>
                    <p className="text-sm text-slate-600">Dauerhaft gesichert</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={TEAM_IMAGE}
                  alt="Professionelle Beratung"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#003B70]/80 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <p className="text-white text-xl font-semibold">Professionelle Beratung</p>
                  <p className="text-white/80">für Ihre Branche maßgeschneidert</p>
                </div>
              </div>
              <div className="absolute -bottom-8 -right-8 bg-[#003B70] text-white p-6 rounded-xl shadow-xl max-w-xs">
                <p className="text-2xl font-bold mb-2">25+ Jahre</p>
                <p className="text-white/80 text-sm">Erfahrung in der Beratung von Architekten und Ingenieuren</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section data-testid="process-section" className="py-24 bg-gradient-to-br from-slate-900 to-[#003B70]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sky-300 font-semibold text-sm uppercase tracking-wider">Unser Prozess</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-4">
              So läuft die Zusammenarbeit
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              Transparent, effizient und auf Ihre Bedürfnisse zugeschnitten.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-sky-400/30 via-white/30 to-sky-400/30" style={{ transform: 'translateY(-50%)' }} />
            
            {processSteps.map((item, index) => (
              <div 
                key={index}
                data-testid={`process-step-${index}`}
                className="relative bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:bg-white/15 transition-all duration-300 group"
              >
                <div className="absolute -top-6 left-8 w-12 h-12 rounded-full bg-gradient-to-br from-sky-400 to-[#003B70] flex items-center justify-center text-white font-bold text-lg shadow-lg group-hover:scale-110 transition-transform">
                  {item.step}
                </div>
                <div className="mt-6">
                  <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center mb-6">
                    <item.icon className="h-7 w-7 text-sky-300" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {item.title}
                  </h3>
                  <p className="text-white/70 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section data-testid="trust-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12 bg-gradient-to-r from-slate-50 to-slate-100 p-10 rounded-2xl">
            <div className="text-center lg:text-left">
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                Persönlich. Verlässlich. Leidenschaftlich.
              </h3>
              <p className="text-slate-600 max-w-lg">
                Zertifizierte Qualität und starke Partner an Ihrer Seite. Als Teil des Konzerns 
                Versicherungskammer Bayern bieten wir Ihnen Sicherheit und Stabilität.
              </p>
            </div>
            <div className="flex items-center gap-10">
              <img 
                src={VKB_LOGO_URL}
                alt="Versicherungskammer Bayern"
                className="h-20 md:h-24 w-auto"
              />
              <div className="h-16 w-px bg-slate-300" />
              <img 
                src={DQS_LOGO_URL}
                alt="DQS Zertifiziert"
                className="h-20 md:h-24 w-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Standort Berlin Section */}
      <section data-testid="standort-section" className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#003B70] font-semibold text-sm uppercase tracking-wider">Unser Standort</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-6">
                Im Herzen von Berlin-Charlottenburg –<br />
                <span className="text-[#003B70]">dort, wo Architektur entsteht</span>
              </h2>
              <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                <p>
                  Unser Standort ist Ausdruck unseres Anspruchs: Mit unserem 
                  Sitz in der Fredericiastraße 6 in Berlin-Charlottenburg positionieren wir uns bewusst 
                  in einem der pulsierendsten und anspruchsvollsten Zentren für Architektur und 
                  Ingenieurwesen in Deutschland. Berlin steht wie kaum eine andere Metropole für 
                  visionäre Stadtentwicklung, architektonische Vielfalt und komplexe 
                  Projektlandschaften, in denen Innovation auf Tradition trifft.
                </p>
                <p>
                  Mit unseren Wurzeln bei der Versicherungskammer Bayern und unserem Sitz in Berlin-Charlottenburg 
                  schlagen wir die Brücke zwischen Süd und Nord – für eine bundesweite Betreuung 
                  von Architekten und Ingenieuren auf höchstem Niveau.
                </p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-2xl shadow-xl max-w-sm mx-auto">
              <img 
                src={MANSFELD_IMAGE}
                alt="Jens-Oliver Mansfeld am Brandenburger Tor in Berlin"
                className="w-full object-cover scale-105 origin-top-left"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#003B70] to-[#001a38]" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.2) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }} />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Lassen Sie uns Ihre Risiken<br />
            <span className="text-sky-300">sauber strukturieren</span>
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Vereinbaren Sie jetzt einen unverbindlichen Beratungstermin und erfahren Sie, 
            wie wir Ihr Planungsbüro optimal absichern können.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/kontakt">
              <Button 
                data-testid="cta-button"
                className="bg-white text-[#003B70] hover:bg-slate-100 px-10 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all"
              >
                Kontakt aufnehmen
              </Button>
            </Link>
            <a href="tel:+493033937950">
              <Button 
                variant="outline"
                className="border-white/40 text-white hover:bg-white/10 px-10 py-6 text-lg"
              >
                <Building2 className="mr-2 h-5 w-5" />
                +49 30 33937950
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
